import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_error, show_success, showBlock } from "lib/helpers"

// agents/index — data-controller="agents--index"
export default class extends Controller {
  static targets = [
    "table",
    "addButton",
    "createModal",
    "createForm",
    "errorBlock",
    "avatarInput",
    "avatarImage",
  ]

  static values = {
    agentsPath: String,
    usersRootPath: String,
    deleteAgentsPath: String,
    isAccountBlocked: { type: Boolean, default: false },
  }

  connect() {
    if (this.hasTableTarget) this.#initDataTable()
    if (this.hasCreateFormTarget) this.#bindCreateForm()
    if (this.hasCreateModalTarget) {
      $(this.createModalTarget).on("hidden.bs.modal.agentsIndex", () => this.#resetCreateForm())
    }
    if (this.hasAvatarInputTarget) this.#bindAvatarPreview()
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
    $(document).off(".agentsIndex")
    $("#check-all-agents").off(".agentsIndex")
    if (this.hasCreateModalTarget) $(this.createModalTarget).off(".agentsIndex")
    if (this.hasAvatarInputTarget) $(this.avatarInputTarget).off(".agentsIndex")
    $(".avatar-uploaded-image").off(".agentsIndex")
    $(".reload-avatar").off(".agentsIndex")
  }

  showCreateModal(event) {
    if (event) event.preventDefault()
    $(this.createModalTarget).modal("show")
  }

  hideCreateModal(event) {
    if (event) event.preventDefault()
    $(this.createModalTarget).modal("hide")
  }

  showRemoveConfirmation(event) {
    if (event) event.preventDefault()
    $("#confirmation_delete_agents").modal("show")
  }

  hideRemoveConfirmation(event) {
    if (event) event.preventDefault()
    $("#confirmation_delete_agents").modal("hide")
  }

  removeAgents(event) {
    if (event) event.preventDefault()

    const groupName = $("input#check-all-agents").val()
    const checkboxes = $(`input[name^='${groupName}']:checked`)
    const available = $(`input[name^='${groupName}']`)

    if (!available.length) return
    if (!checkboxes.length) {
      alert("Please select at least one item")
      return
    }

    $(".dataTables_processing").css("display", "block")

    const ids = $.makeArray(checkboxes.map(function () { return $(this).val() }))

    $.delete(this.deleteAgentsPathValue, { item_ids: ids }, (response) => {
      this.hideRemoveConfirmation()
      show_success("Agents removed successfully")
      this.dataTable.ajax.reload()
      if (!response.agents_limits_exceeded) {
        $(".head-toolbar").html(
          '<a id="add-agent-button" class="new-btn" href="#" data-action="click->agents--index#showCreateModal">Create</a>',
        )
      }
      if (response.allow_creating_admin_agent) {
        const exists = $("#new_agent_form_create_for_me").length > 0
        if (!exists) {
          $("#new_agent_form_control_buttons").prepend(
            '<input name="new_agent_form[create_for_me]" type="hidden" value="0" autocomplete="off" />' +
              '<input class="custom-checkbox" id="new_agent_form_create_for_me" type="checkbox" value="1" name="new_agent_form[create_for_me]" />' +
              '<label class="create-for-me-label" for="new_agent_form_create_for_me" id="new_agent_form_create_for_me_label">Create agent profile for me</label>',
          )
        } else {
          $("#new_agent_form_create_for_me").val(0).prop("checked", false).show()
          $("#new_agent_form_create_for_me_label").show()
        }
      }
    }).fail(() => {
      this.hideRemoveConfirmation()
      show_error("An error occurred during request")
    })

    $("#check-all-agents").prop("checked", false)
  }

  generatePassword(event) {
    event.preventDefault()
    $("#new_agent_form_password").val(this.#generateAlphanumericPassword())
  }

  avatarChanged = (event) => {
    if (event.currentTarget.disabled) {
      showBlock(this.errorBlockTarget, "File upload not supported!")
      return
    }
    const files = event.currentTarget.files
    if (files && files[0]) this.#readImage(files[0])
  }

  #bindCreateForm() {
    $(this.createFormTarget).ajaxForm({
      dataType: "json",
      success: (response) => {
        if (response.success) {
          show_success("Agent created successfully")
          if (response.hide_create_for_me) {
            $("#new_agent_form_create_for_me").hide().prop("checked", false).val(false)
            $("#new_agent_form_create_for_me_label").hide()
          }
          this.dataTable.ajax.reload()
        } else {
          const error = $(this.errorBlockTarget)
          if (error.length === 1) error.after(error.clone())
          showBlock(this.errorBlockTarget, response.message)
        }
        $.scrollTo("body", "slow")
        this.createFormTarget.reset()
        $(this.createFormTarget).find("input[type='submit']").enable(true)
        this.hideCreateModal()
      },
      error: () => {
        $.scrollTo("body", "slow")
        $(this.createFormTarget).find("input[type='submit']").enable(true)
        this.hideCreateModal()
        showBlock(this.errorBlockTarget, "An error occurred during request")
      },
    })
  }

  #initDataTable() {
    const usersRoot = this.usersRootPathValue

    this.dataTable = createTurboDataTable(this.tableTarget, {
      dom: 'l<"head-toolbar">rtip<"footer-toolbar">',
      scrollX: true,
      pagingType: "full_numbers",
      processing: true,
      serverSide: true,
      autoWidth: false,
      bSort: false,
      responsive: true,
      language: { emptyTable: "You haven't any agents yet. Please create one." },
      ajax: { url: this.agentsPathValue },
      footerCallback: () => {
        $("#check-all-agents").attr("checked", false)
      },
      drawCallback: () => {
        $(".copy-user-id").off("click.agentsIndex").on("click.agentsIndex", (e) => this.#copyUserId(e))
      },
      columnDefs: [
        {
          targets: 0,
          orderable: false,
          width: "28px",
          createdCell: (cell, cellData) => {
            $(cell).html(
              `<input type="checkbox" class="table-checkbox-agents" id="check-agent${cellData}" name="item_ids[]" value="${cellData}"/> <label for="check-agent${cellData}"></label>`,
            )
          },
        },
        {
          targets: 1,
          orderable: false,
          width: "230px",
          createdCell: (cell, cellData) => {
            $(cell).html(
              `<a href="${usersRoot}/edit/${cellData}" class="copy-text" data-user-id="${cellData}">${cellData}` +
                `<span class="copy-user-id-span"><i class="copy-user-id bg-copy" data-user-id="${cellData}"/></span></a>`,
            )
          },
        },
        {
          targets: 2,
          createdCell: (cell, cellData) => {
            $(cell).html(cellData)
          },
        },
        {
          targets: 3,
          createdCell: (cell, cellData) => {
            if (cellData === "true" || cellData === true) {
              $(cell).html('<span class="agents-list-online-status online"></span>Online')
            } else {
              $(cell).html('<span class="agents-list-online-status offline"></span>Offline')
            }
          },
        },
      ],
    })

    $(".head-toolbar").append($(this.addButtonTarget).clone().removeClass("hide_button"))

    if ($("#reader").val() === "false" && !this.isAccountBlockedValue) {
      $(".footer-toolbar").html(
        '<br><br><a href="#" class="remove" data-action="click->agents--index#showRemoveConfirmation">Remove selected</a>',
      )
    }

    $("#check-all-agents").on("change.agentsIndex", function () {
      const isChecked = $(this).prop("checked")
      $(".table-checkbox-agents").prop("checked", isChecked)
      $(".table-checkbox-agents").closest("tr").toggleClass("selected-row", isChecked)
    })

    $(document).on("change.agentsIndex", ".table-checkbox-agents", function () {
      $(this).closest("tr").toggleClass("selected-row", $(this).prop("checked"))
      const selectedCount = $("input.table-checkbox-agents:checked").length
      const totalCount = $("input.table-checkbox-agents").length
      $("#check-all-agents").prop("checked", selectedCount > 0 && selectedCount === totalCount)
    })
  }

  #copyUserId(event) {
    event.preventDefault()
    const userId = $(event.currentTarget).data("user-id")
    const tempTextArea = document.createElement("textarea")
    tempTextArea.value = userId
    document.body.appendChild(tempTextArea)
    tempTextArea.select()
    const success = document.execCommand("copy")
    document.body.removeChild(tempTextArea)

    const $el = $(event.currentTarget)
    if (success === true) {
      $el.addClass("copy-success")
      setTimeout(() => $el.removeClass("copy-success"), 2000)
    } else {
      $el.addClass("copy-error")
      setTimeout(() => $el.removeClass("copy-error"), 2000)
      console.error("Failed to copy userID to clipboard")
    }
  }

  #bindAvatarPreview() {
    $(this.avatarInputTarget).on("change.agentsIndex", this.avatarChanged)
  }

  #readImage(file) {
    if (file.size > 1048576) {
      showBlock(this.errorBlockTarget, `Image is too large (current: ${file.size} Byte, max: 1MB (1048576)`)
      $(this.avatarInputTarget).val("")
      return
    }

    const reader = new FileReader()
    const image = new Image()

    reader.readAsDataURL(file)
    reader.onload = (_file) => {
      image.src = _file.target.result
      image.onload = () => {
        $(".avatar-uploaded-image").removeClass("hide")
        $(this.avatarImageTarget).attr("src", image.src)
        this.#bindHoverAvatar()
      }
      image.onerror = () => {
        showBlock(this.errorBlockTarget, `Invalid file type: ${file.type}`)
        $(this.avatarInputTarget).val("")
      }
    }
  }

  #bindHoverAvatar() {
    $(".avatar-uploaded-image").on("mouseenter.agentsIndex", () => {
      $(".reload-avatar").removeClass("hide")
      $(".avatar-uploaded-image").off("mouseenter.agentsIndex")
      this.#bindRemoveAvatar()
    })
  }

  #bindRemoveAvatar() {
    $(".reload-avatar")
      .on("mouseleave.agentsIndex", () => {
        $(".reload-avatar").addClass("hide").off("mouseleave.agentsIndex click.agentsIndex")
        this.#bindHoverAvatar()
      })
      .on("click.agentsIndex", () => {
        const defaultUrl = $(this.avatarImageTarget).data("default-url")
        $(this.avatarImageTarget).attr("src", defaultUrl)
        $(this.avatarInputTarget).val("")
        $(".reload-avatar").off("mouseleave.agentsIndex click.agentsIndex")
        $(".avatar-uploaded-image").off("mouseenter.agentsIndex").addClass("hide")
        $(".reload-avatar").addClass("hide")
      })
  }

  #resetCreateForm() {
    $("#new_agent_form_password").val("")
    $("#new_agent_form_name").val("")
    $("#new_agent_form_email").val("")
    $("#new_agent_form_login").val("")
  }

  #generateAlphanumericPassword() {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    let password = ""
    for (let i = 0; i < 32; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }
    return password
  }
}
